import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'

import { TooltipContextProvider } from '../../common/tooltip/tooltip.context'
import { Tooltip } from '../../common/tooltip/tooltip'
import { ForestAxes } from './forest-axes'
import { forestService } from './forest.service'
import { Legend } from '../../common/legend/legend'
import { ForestSwimlane } from './forest-swimlane'
import { ForestHoverLine } from './forest-hover-line'

import { useStyles, BORDER_WIDTH, DATA_ROW_HEIGHT } from './forest.style'

const BOTTOM_AXIS_HEIGHT = 46

export const ForestPlotContainer = function ({ children, data, datasets, columnNames }) {
  const [analysesList, setAnalysesList] = useState([])
  const [labels, setLabels] = useState()
  const [colors, setColors] = useState()

  useEffect(() => {
    const dataList = datasets || [data]

    if (dataList && dataList[0]) {
      const analysesList = []
      const labels = []
      const colors = []
      dataList.forEach((data) => {
        analysesList.push(forestService.mapData(data, columnNames))
      })
      analysesList.forEach((analyses) => {
        if (analyses[0] && analyses[0].groups) {
          labels.push(...analyses[0].groups.map((g) => g.label).filter((label) => !labels.includes(label)))
          colors.push(...analyses[0].groups.map((g) => g.color).filter((color) => !labels.includes(color)))
        }
      })
      labels.sort()

      setAnalysesList(analysesList)
      setLabels(labels)
      setColors(colors)
    }
  }, [ data, datasets ])

  return <>{children({ analysesList, labels, colors })}</>
}

export const ForestPlot = function (props = {}) {
  return (
    <ForestPlotContainer {...props}>
      {({ analysesList, labels, colors }) => {
        const analyses = analysesList[0]
        const showLegend = analyses && analyses.length > 1

        return (
          <>
            {showLegend && <Legend labels={analyses[0].groups.map((g) => g.label)} colors={analyses[0].groups.map((g) => g.color)} />}
            <SingleForestPlot analyses={analyses} labels={labels} colors={colors} {...props} />
          </>
        )
      }}
    </ForestPlotContainer>
  )
}

export const SingleForestPlot = function ({
  analyses,
  axisLabels = { x: 'Risk' },
  groupLabel = 'Group',
  xScaleType,
  xValueDomain,
  xValueNullEffectLine
}) {
  const classes = useStyles()

  const [scales, setScales] = useState()
  const [chartWidth, setChartWidth] = useState(0)
  const [chartHeight, setChartHeight] = useState(0)

  const adjustedChartWidth = (chartWidth > 0) ? chartWidth : 0
  const chartDataHeight = analyses ? analyses.reduce((accumulator, analysis) => {
    return accumulator + (analysis.groups.length * DATA_ROW_HEIGHT)
  }, 0) : 0

  if (xValueNullEffectLine && typeof xValueNullEffectLine === 'string') {
    xValueNullEffectLine = xValueNullEffectLine.trim()
  }
  const hasNullEffectLine = xValueNullEffectLine !== undefined && xValueNullEffectLine !== null && xValueNullEffectLine !== ''

  const chartContainerRef = (div) => {
    if (div) {
      setChartWidth(div.clientWidth)
    }
  }

  const chartParentGroupRef = (gElement) => {
    if (gElement) {
      const dimensions = gElement.getBBox()
      const height = dimensions && dimensions.height
      setChartHeight(height)
    }
  }

  useEffect(() => {
    if (!analyses) {
      return
    }

    const scales = {
      x: forestService.getXScale({ analyses, width: chartWidth, scaleType: xScaleType, xValueDomain })
    }
    setScales(scales)
  }, [ analyses, chartWidth, xValueDomain ])

  if (!analyses || !scales) return null

  if (analyses.length > 1) {
    return (
      <TooltipContextProvider>
        <div ref={chartContainerRef}>
          <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ overflow: 'visible' }}>
            <defs>
              <filter id="dropshadow" x="0" y="0" width="200%" height="200%">
                <feDropShadow dx="2" dy="2" stdDeviation="1" floodColor="lightgray" />
              </filter>
            </defs>
            <g transform={`translate(${BORDER_WIDTH}, ${BORDER_WIDTH})`} ref={chartParentGroupRef}>
              <ForestAxes
                labels={axisLabels}
                dimensions={{ width: adjustedChartWidth, height: chartDataHeight }}
                xScale={scales.x}
              />
              { analyses.map((analysis, index) => (
                <g key={analysis.key} transform={`translate(0, ${index * DATA_ROW_HEIGHT * analysis.groups.length})`}>
                  <ForestSwimlane
                    analysis={analysis}
                    xScale={scales.x}
                    width={adjustedChartWidth}
                    rowHeight={DATA_ROW_HEIGHT}
                    hideBottomLine={index < (analyses.length - 1)}
                    hasMultipleSwimlanes={true}
                  />
                </g>
              ))}
              {hasNullEffectLine && (
                <line
                  className={classes.nullEffectLine}
                  y1={0}
                  y2={chartDataHeight}
                  x1={scales.x(xValueNullEffectLine)}
                  x2={scales.x(xValueNullEffectLine)}
                />
              )}
              <ForestHoverLine
                dimensions={{ width: adjustedChartWidth, height: chartDataHeight }}
                leftAxisWidth={BORDER_WIDTH}
                bottomAxisHeight={BOTTOM_AXIS_HEIGHT}
              />
            </g>
          </svg>
          <Tooltip defaults={{ primaryLabel: axisLabels.x, secondaryLabel: groupLabel }} />
        </div>
      </TooltipContextProvider>
    )
  } else {
    const analysis = analyses[0]

    return (
      <TooltipContextProvider>
        <div className={classes.root}>
          <div className={classes.labelContainer}>
            {
              analysis.groups.map((group) => {
                return (
                  <div key={group.key} className={classes.labelText}>{group.label}</div>
                )
              })
            }
          </div>
          <div className={classes.chartContainer} ref={chartContainerRef}>
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className={classes.svg} style={{ overflow: 'visible' }}>
              <defs>
                <filter id="dropshadow" x="0" y="0" width="200%" height="200%">
                  <feDropShadow dx="2" dy="2" stdDeviation="1" floodColor="lightgray" />
                </filter>
              </defs>
              <g transform={`translate(${BORDER_WIDTH}, ${BORDER_WIDTH})`} ref={chartParentGroupRef}>
                <ForestAxes
                  labels={axisLabels}
                  dimensions={{ width: adjustedChartWidth, height: chartDataHeight }}
                  xScale={scales.x}
                />
                <ForestSwimlane
                  key={analysis.key}
                  analysis={analysis}
                  xScale={scales.x}
                  width={adjustedChartWidth}
                  rowHeight={DATA_ROW_HEIGHT}
                />
                {hasNullEffectLine && (
                  <line
                    className={classes.nullEffectLine}
                    y1={0}
                    y2={chartDataHeight}
                    x1={scales.x(xValueNullEffectLine)}
                    x2={scales.x(xValueNullEffectLine)}
                  />
                )}
                <ForestHoverLine
                  dimensions={{ width: adjustedChartWidth, height: chartDataHeight }}
                  leftAxisWidth={BORDER_WIDTH}
                  bottomAxisHeight={BOTTOM_AXIS_HEIGHT}
                />
              </g>
            </svg>
          </div>
        </div>
        <Tooltip defaults={{ primaryLabel: axisLabels.x, secondaryLabel: groupLabel }} />
      </TooltipContextProvider>
    )
  }
}

export async function forestWidget (id, props) {
  const element = document.getElementById(id)

  ReactDOM.render(
    React.createElement(ForestPlot, props),
    element
  )
}
